/**
 * WaiverSignForm — one waiver, read and signed.
 *
 * Shared by the emailed-link page (parent not signed in) and the prompt a
 * signed-in parent sees, so both collect exactly the same thing: the
 * waiver's own fields, an explicit "I agree", and the signer's typed name.
 * The parent supplies `onSign`; this component does not know which door the
 * family came through.
 */
import React, { useState } from "react";
import { Button, Checkbox, Input } from "../ui";
import { useTheme } from "../../context/ThemeContext";
import { getPrimaryText, getSecondaryText, getLightBorder } from "../../utils/themeUtils";
import type { PendingWaiver, SignAnswers, WaiverBlock } from "../../api/waiverRequestsAPI";

interface WaiverSignFormProps {
  waiver: PendingWaiver;
  /** Resolves when the signature is recorded; rejects with a message to show. */
  onSign: (answers: SignAnswers) => Promise<void>;
  /** Shown above the agree box, e.g. "Signing for: Ari, Bo". */
  signingFor?: string | null;
}

const isBlank = (value: string | boolean | undefined) =>
  value === undefined || value === false || (typeof value === "string" && value.trim() === "");

const WaiverSignForm: React.FC<WaiverSignFormProps> = ({ waiver, onSign, signingFor }) => {
  const { isDarkMode } = useTheme();
  const [fields, setFields] = useState<Record<string, string | boolean>>({});
  const [agreed, setAgreed] = useState(false);
  // "No", offered only when the club lets families decline this waiver.
  const [declined, setDeclined] = useState(false);
  const canDecline = waiver.allow_decline === true;
  const [signerName, setSignerName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField = (id: string, value: string | boolean) => setFields((prev) => ({ ...prev, [id]: value }));

  const missingRequired = (waiver.blocks || []).find(
    (b) => b.type === "form_field" && b.required && isBlank(fields[b.id])
  );
  const named = signerName.trim().length >= 2;
  // A "no" needs only the person's name: the waiver's own fields belong to a "yes".
  const canSign = !saving && named && (declined ? canDecline : agreed && !missingRequired);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSign) return;
    setSaving(true);
    setError(null);
    try {
      await onSign(
        declined
          ? { signer_name: signerName.trim(), agreed: false, declined: true, fields: {} }
          : { signer_name: signerName.trim(), agreed: true, fields }
      );
    } catch (err: any) {
      setError(err?.message || "We could not record your signature. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const renderBlock = (block: WaiverBlock) => {
    if (block.type === "heading") {
      return (
        <h3 key={block.id} className={`text-lg font-bold ${getPrimaryText(isDarkMode)}`}>
          {block.content}
        </h3>
      );
    }
    if (block.type === "text") {
      return (
        <p key={block.id} className={`whitespace-pre-wrap ${getSecondaryText(isDarkMode)}`}>
          {block.content}
        </p>
      );
    }
    if (block.type !== "form_field") return null;

    const label = block.field_label || "Answer";
    if (block.field_type === "checkbox") {
      return (
        <Checkbox
          key={block.id}
          label={
            <>
              {label}
              {block.required && <span className="text-red-500 ml-1">*</span>}
            </>
          }
          checked={fields[block.id] === true}
          onChange={(e) => setField(block.id, e.target.checked)}
        />
      );
    }
    return (
      <Input
        key={block.id}
        label={label}
        type={block.field_type === "date" ? "date" : "text"}
        required={!!block.required}
        requiredMarker={!!block.required}
        hint={block.field_type === "signature" ? "Type your full name as your signature." : undefined}
        value={typeof fields[block.id] === "string" ? (fields[block.id] as string) : ""}
        onChange={(e) => setField(block.id, e.target.value)}
      />
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" aria-label={`Sign ${waiver.name}`}>
      <div className="space-y-3">
        {(waiver.blocks || []).length === 0 ? (
          <p className={getSecondaryText(isDarkMode)}>This form has no text yet. Contact the club before signing.</p>
        ) : (
          waiver.blocks.map(renderBlock)
        )}
      </div>

      <div className={`pt-4 border-t ${getLightBorder(isDarkMode)} space-y-4`}>
        {signingFor && <p className={`text-sm ${getSecondaryText(isDarkMode)}`}>{signingFor}</p>}
        {canDecline ? (
          <fieldset className="space-y-2" data-testid="waiver-sign-yes-no">
            <legend className={`text-sm font-medium ${getPrimaryText(isDarkMode)}`}>
              Please answer yes or no.
            </legend>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name={`waiver-sign-answer-${waiver.waiver_id}`}
                className="mt-0.5 h-4 w-4 accent-[var(--cc-action)]"
                checked={agreed}
                onChange={() => {
                  setAgreed(true);
                  setDeclined(false);
                }}
              />
              <span className={`text-sm ${getPrimaryText(isDarkMode)}`}>
                Yes, I have read and agree to "{waiver.name}".
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name={`waiver-sign-answer-${waiver.waiver_id}`}
                className="mt-0.5 h-4 w-4 accent-[var(--cc-action)]"
                checked={declined}
                onChange={() => {
                  setDeclined(true);
                  setAgreed(false);
                }}
              />
              <span className={`text-sm ${getPrimaryText(isDarkMode)}`}>No, I do not agree.</span>
            </label>
          </fieldset>
        ) : (
          <Checkbox
            label={`I have read and agree to "${waiver.name}".`}
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
        )}
        <Input
          label="Your full name"
          hint={declined ? "Typing your name here records your answer." : "Typing your name here is your signature."}
          autoComplete="name"
          required
          requiredMarker
          value={signerName}
          onChange={(e) => setSignerName(e.target.value)}
        />
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" size="lg" disabled={!canSign} loading={saving}>
          {declined ? "Submit my answer" : "Sign"}
        </Button>
      </div>
    </form>
  );
};

export default WaiverSignForm;
